import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { MessagesPanel } from "../../components/admin/MessagesPanel";
import { ContentEditor } from "../../components/admin/ContentEditor";
import { ThemeEditor } from "../../components/admin/ThemeEditor";
import { SettingsPanel } from "../../components/admin/SettingsPanel";
import { Inbox, FileEdit, LogOut, ExternalLink, Palette, Settings } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("messages");

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const tabs = [
    { id: "messages", label: "Messages", icon: Inbox },
    { id: "content", label: "Content", icon: FileEdit },
    { id: "theme", label: "Theme", icon: Palette },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-ink flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-72 md:min-h-screen border-b md:border-b-0 md:border-r border-line bg-void p-6 flex md:flex-col justify-between">
        <div className="w-full">
          <div className="mb-10">
            <span className="font-display text-xl tracking-tight">
              Mixing Dreams <span className="text-coral">Ltd</span>
            </span>
            <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-smoke">Admin Panel</p>
          </div>
          <nav className="flex md:flex-col gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm transition-colors ${
                  tab === t.id
                    ? "bg-gold/10 text-gold"
                    : "text-smoke hover:bg-surface hover:text-ink"
                }`}
              >
                <t.icon size={18} /> {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="hidden md:flex flex-col gap-2 w-full">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-smoke transition-colors hover:bg-surface hover:text-ink"
          >
            <ExternalLink size={18} /> View Website
          </a>
          <div className="border-t border-line pt-3">
            <p className="px-4 text-xs text-[#555] mb-2 truncate">{user?.email}</p>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-smoke transition-colors hover:bg-coral/10 hover:text-coral"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="md:hidden flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-coral"
        >
          <LogOut size={18} />
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 max-w-5xl">
        {tab === "messages" && <MessagesPanel />}
        {tab === "content" && <ContentEditor />}
        {tab === "theme" && <ThemeEditor />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}
